import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from "@tanstack/react-query";
import { GetUser } from "@/components/api/queries";
import { Button } from "@/components/ui/button";
// import "../../../styles/admin-tools.css";

import {
    Settings,
    Bug,
    Eye,
    EyeOff,
    ChevronUp,
    ChevronDown,
    AlertTriangle,
    Database,
    Zap,
    Activity,
    Terminal,
    Network,
    Trash2,
    Minimize2,
    Archive,
    Cookie,
    ExternalLink
} from "lucide-react";

interface AdminToolsProps {
    showDevtools: boolean;
    onToggleDevtools: () => void;
}

interface ConsoleEntry {
    id: string;
    timestamp: Date;
    type: 'log' | 'warn' | 'error' | 'info';
    message: string;
    args: any[];
}

interface NetworkEntry {
    id: string;
    timestamp: Date;
    method: string;
    url: string;
    status?: number;
    duration?: number;
    size?: number;
}

interface PerformanceMetrics {
    pageLoadTime: number;
    domContentLoaded: number;
    firstContentfulPaint: number;
    renderCount: number;
    lastRenderTime: number;
}

interface Position {
    x: number;
    y: number;
}

function AdminTools({ showDevtools, onToggleDevtools }: AdminToolsProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [activeTab, setActiveTab] = useState<'main' | 'analytics' | 'console' | 'network'>('main');
    const [position, setPosition] = useState<Position>({ x: 16, y: 16 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);
    const [networkEntries, setNetworkEntries] = useState<NetworkEntry[]>([]);
    const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
        pageLoadTime: 0,
        domContentLoaded: 0,
        firstContentfulPaint: 0,
        renderCount: 0,
        lastRenderTime: Date.now()
    });

    const dragRef = useRef<HTMLDivElement>(null);
    const renderCountRef = useRef<number>(0);

    // Load position from localStorage
    useEffect(() => {
        const savedPosition = localStorage.getItem('adminToolsPosition');
        if (savedPosition) {
            try {
                const parsedPosition = JSON.parse(savedPosition);
                setPosition(parsedPosition);
            } catch (error) {
                console.warn('Failed to parse saved admin tools position:', error);
            }
        }
    }, []);

    // Save position to localStorage
    const savePosition = useCallback((newPosition: Position) => {
        localStorage.setItem('adminToolsPosition', JSON.stringify(newPosition));
        setPosition(newPosition);
    }, []);

    // Drag functionality
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.target !== e.currentTarget && !(e.target as HTMLElement).closest('.drag-handle')) return;

        setIsDragging(true);
        const rect = dragRef.current?.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;

        const newPosition = {
            x: Math.max(0, Math.min(window.innerWidth - 300, e.clientX - dragOffset.x)),
            y: Math.max(0, Math.min(window.innerHeight - 200, e.clientY - dragOffset.y))
        };

        savePosition(newPosition);
        e.preventDefault();
    }, [isDragging, dragOffset, savePosition]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Setup drag event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Console monitoring - only in development
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        const originalLog = console.log;
        const originalWarn = console.warn;
        const originalError = console.error;
        const originalInfo = console.info;

        const addConsoleEntry = (type: ConsoleEntry['type'], args: any[]) => {
            const entry: ConsoleEntry = {
                id: `${Date.now()}-${Math.random()}`,
                timestamp: new Date(),
                type,
                message: args.map(arg =>
                    typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
                ).join(' '),
                args
            };

            setConsoleEntries(prev => [...prev.slice(-99), entry]); // Keep last 100 entries
        };

        console.log = (...args) => {
            originalLog.apply(console, args);
            addConsoleEntry('log', args);
        };

        console.warn = (...args) => {
            originalWarn.apply(console, args);
            addConsoleEntry('warn', args);
        };

        console.error = (...args) => {
            originalError.apply(console, args);
            addConsoleEntry('error', args);
        };

        console.info = (...args) => {
            originalInfo.apply(console, args);
            addConsoleEntry('info', args);
        };

        return () => {
            console.log = originalLog;
            console.warn = originalWarn;
            console.error = originalError;
            console.info = originalInfo;
        };
    }, []);

    // Network monitoring - only in development
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        const originalFetch = window.fetch;

        window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
            const startTime = Date.now();
            const url = typeof input === 'string' ? input :
                input instanceof URL ? input.toString() :
                    input.url;
            const method = init?.method || 'GET';

            const networkEntry: NetworkEntry = {
                id: `${Date.now()}-${Math.random()}`,
                timestamp: new Date(),
                method,
                url
            };

            try {
                const response = await originalFetch(input, init);
                const endTime = Date.now();

                networkEntry.status = response.status;
                networkEntry.duration = endTime - startTime;
                networkEntry.size = parseInt(response.headers.get('content-length') || '0');

                setNetworkEntries(prev => [...prev.slice(-49), networkEntry]); // Keep last 50 entries

                return response;
            } catch (error) {
                const endTime = Date.now();
                networkEntry.duration = endTime - startTime;
                networkEntry.status = 0;

                setNetworkEntries(prev => [...prev.slice(-49), networkEntry]);
                throw error;
            }
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, []);

    // Performance monitoring
    useEffect(() => {
        renderCountRef.current += 1;

        const updateMetrics = () => {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
            const paint = performance.getEntriesByType('paint');

            setPerformanceMetrics(prev => ({
                ...prev,
                pageLoadTime: navigation ? navigation.loadEventEnd - navigation.fetchStart : prev.pageLoadTime,
                domContentLoaded: navigation ? navigation.domContentLoadedEventEnd - navigation.fetchStart : prev.domContentLoaded,
                firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime || prev.firstContentfulPaint,
                renderCount: renderCountRef.current,
                lastRenderTime: Date.now()
            }));
        };

        // Only update metrics on mount and when document is ready
        if (document.readyState === 'complete') {
            updateMetrics();
        } else {
            const handleLoad = () => updateMetrics();
            window.addEventListener('load', handleLoad);
            return () => window.removeEventListener('load', handleLoad);
        }
    }, []); // Empty dependency array to prevent infinite re-renders

    const clearConsole = () => setConsoleEntries([]);
    const clearNetwork = () => setNetworkEntries([]);

    // Check if user is Administrator or on localhost
    const { data: userData } = useQuery({
        queryKey: ['user'],
        queryFn: GetUser,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const isAdministrator = userData?.result?.rolls?.includes('Administrators');
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.endsWith('.local');

    // Show admin tools if user is administrator OR on localhost (for development)
    const shouldShowAdminTools = isAdministrator || isLocalhost;

    // Debug logging - only in development
    React.useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔧 AdminTools Debug:');
            console.log('  - Hostname:', window.location.hostname);
            console.log('  - Is Localhost:', isLocalhost);
            console.log('  - User Data:', userData);
            console.log('  - Is Administrator:', isAdministrator);
            console.log('  - Should Show Admin Tools:', shouldShowAdminTools);
        }
    }, [isLocalhost, isAdministrator, shouldShowAdminTools, userData]);

    // Don't render if user is not an administrator and not on localhost
    if (!shouldShowAdminTools) {
        // Only log once to prevent console spam in production
        if (process.env.NODE_ENV === 'development') {
            console.log('❌ AdminTools: Not showing - user is not admin and not on localhost');
        }
        return null;
    }



    const forceError = () => {
        // Force a JavaScript error for testing ErrorBoundary
        throw new Error('Test error triggered by Administrator - This is intentional for testing purposes');
    };

    const forceAsyncError = () => {
        // Force an async error that won't be caught by ErrorBoundary
        setTimeout(() => {
            throw new Error('Test async error triggered by Administrator - This won\'t be caught by ErrorBoundary');
        }, 100);
    };

    const dumpCache = () => {
        console.group('🗄️ Cache Dump');

        // Local Storage
        console.log('Local Storage:');
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                try {
                    const value = localStorage.getItem(key);
                    console.log(`  ${key}:`, value);
                } catch (e) {
                    console.log(`  ${key}: [Error reading value]`);
                }
            }
        }

        // Session Storage
        console.log('\nSession Storage:');
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
                try {
                    const value = sessionStorage.getItem(key);
                    console.log(`  ${key}:`, value);
                } catch (e) {
                    console.log(`  ${key}: [Error reading value]`);
                }
            }
        }

        // React Query Cache (if available)
        console.log('\nReact Query Cache: Check Network tab for cache status');

        console.groupEnd();
    };

    const dumpCookies = () => {
        console.group('🍪 Cookie Dump');

        const cookies = document.cookie.split(';');
        if (cookies.length === 1 && cookies[0] === '') {
            console.log('No cookies found');
        } else {
            cookies.forEach(cookie => {
                const [name, ...valueParts] = cookie.trim().split('=');
                const value = valueParts.join('=');
                console.log(`${name}:`, value);
            });
        }

        console.groupEnd();
    };

    const openQuickLink = (url: string, label: string) => {
        console.log(`🔗 Opening ${label}:`, url);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const logDebugInfo = () => {
        console.group('🔧 Admin Debug Information');
        console.log('User Data:', userData);
        console.log('Is Administrator:', isAdministrator);
        console.log('Is Localhost:', isLocalhost);
        console.log('Should Show Admin Tools:', shouldShowAdminTools);
        console.log('DevTools State:', showDevtools);
        console.log('Environment:', process.env.NODE_ENV);
        console.log('Hostname:', window.location.hostname);
        console.log('Performance Metrics:', performanceMetrics);
        console.log('Console Entries:', consoleEntries.length);
        console.log('Network Entries:', networkEntries.length);
        console.groupEnd();
    };

    const formatTime = (time: number) => {
        if (time < 1000) return `${time.toFixed(0)}ms`;
        return `${(time / 1000).toFixed(2)}s`;
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes}B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    };

    if (!isVisible) {
        // Minimized floating button
        return (
            <div
                className="admin-tools-minimized"
                style={{ left: position.x, top: position.y }}
            >
                <Button
                    onClick={() => setIsVisible(true)}
                    className="w-12 h-12 rounded-full bg-gradient-to-r from-gray-900 to-red-600 hover:from-black hover:to-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
                    title="Admin Tools"
                >
                    <Settings className="w-5 h-5" />
                </Button>
            </div>
        );
    }

    return (
        <div
            ref={dragRef}
            className="admin-tools-panel"
            style={{ left: position.x, top: position.y }}
        >
            <div className="bg-black/95 backdrop-blur-sm border border-red-500/30 rounded-xl shadow-2xl overflow-hidden min-w-[320px] max-w-[480px]">
                {/* Header with drag handle */}
                <div
                    className="drag-handle flex items-center justify-between p-3 bg-gradient-to-r from-red-600/20 to-gray-800/20 border-b border-red-500/20 cursor-move"
                    onMouseDown={handleMouseDown}
                >
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                            <Settings className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold text-white">Admin Tools</span>
                            <span className="text-xs text-red-300">
                                {isAdministrator ? 'Administrator' : 'Localhost Dev'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            onClick={() => setIsExpanded(!isExpanded)}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-300 hover:text-white hover:bg-red-600/20"
                        >
                            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                        </Button>
                        <Button
                            onClick={() => setIsVisible(false)}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-300 hover:text-white hover:bg-red-600/20"
                        >
                            <Minimize2 className="w-3 h-3" />
                        </Button>
                    </div>
                </div>

                {/* Tab Navigation */}
                {isExpanded && (
                    <>
                        <div className="flex border-b border-red-500/20 bg-gray-900/50">
                            {[
                                { id: 'main', label: 'Tools', icon: Settings },
                                { id: 'analytics', label: 'Analytics', icon: Activity },
                                { id: 'console', label: 'Console', icon: Terminal },
                                { id: 'network', label: 'Network', icon: Network }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs transition-colors ${activeTab === tab.id
                                        ? 'bg-red-600/20 text-red-300 border-b-2 border-red-500'
                                        : 'text-gray-400 hover:text-red-300 hover:bg-red-600/10'
                                        }`}
                                >
                                    <tab.icon className="w-3 h-3" />
                                    {tab.label}
                                    {tab.id === 'console' && consoleEntries.length > 0 && (
                                        <span className="bg-red-600 text-white text-[10px] px-1 rounded-full min-w-[16px] h-4 flex items-center justify-center">
                                            {consoleEntries.length > 99 ? '99+' : consoleEntries.length}
                                        </span>
                                    )}
                                    {tab.id === 'network' && networkEntries.length > 0 && (
                                        <span className="bg-red-600 text-white text-[10px] px-1 rounded-full min-w-[16px] h-4 flex items-center justify-center">
                                            {networkEntries.length > 99 ? '99+' : networkEntries.length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="p-3 max-h-[400px] overflow-y-auto">
                            {activeTab === 'main' && (
                                <div className="space-y-3">
                                    {/* DevTools Toggle */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">DevTools</label>
                                        <Button
                                            onClick={onToggleDevtools}
                                            variant="outline"
                                            size="sm"
                                            className={`w-full justify-between text-xs ${showDevtools
                                                ? 'border-green-500/50 bg-green-900/20 text-green-300 hover:bg-green-900/30'
                                                : 'border-gray-600 text-gray-300 hover:bg-gray-800'
                                                }`}
                                        >
                                            <span className="flex items-center gap-2">
                                                {showDevtools ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                React Query DevTools
                                            </span>
                                            <span className={`text-xs px-1.5 py-0.5 rounded ${showDevtools ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                                                }`}>
                                                {showDevtools ? 'ON' : 'OFF'}
                                            </span>
                                        </Button>
                                    </div>

                                    {/* Error Testing */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">Error Testing</label>
                                        <div className="space-y-1">
                                            <Button
                                                onClick={forceError}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-red-500/50 text-red-300 hover:bg-red-900/20 hover:border-red-500"
                                            >
                                                <Bug className="w-3 h-3 mr-2" />
                                                Force Error (Sync)
                                            </Button>
                                            <Button
                                                onClick={forceAsyncError}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-orange-500/50 text-orange-300 hover:bg-orange-900/20 hover:border-orange-500"
                                            >
                                                <Zap className="w-3 h-3 mr-2" />
                                                Force Error (Async)
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Debug Actions */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">Debug</label>
                                        <div className="space-y-1">
                                            <Button
                                                onClick={logDebugInfo}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-blue-500/50 text-blue-300 hover:bg-blue-900/20 hover:border-blue-500"
                                            >
                                                <Database className="w-3 h-3 mr-2" />
                                                Log Debug Info
                                            </Button>
                                            <Button
                                                onClick={dumpCache}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-purple-500/50 text-purple-300 hover:bg-purple-900/20 hover:border-purple-500"
                                            >
                                                <Archive className="w-3 h-3 mr-2" />
                                                Dump Cache
                                            </Button>
                                            <Button
                                                onClick={dumpCookies}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-green-500/50 text-green-300 hover:bg-green-900/20 hover:border-green-500"
                                            >
                                                <Cookie className="w-3 h-3 mr-2" />
                                                Dump Cookies
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Quick Links */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">Quick Links</label>
                                        <div className="space-y-1">
                                            <Button
                                                onClick={() => openQuickLink('/admin', 'Admin Dashboard')}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/20 hover:border-cyan-500"
                                            >
                                                <Settings className="w-3 h-3 mr-2" />
                                                Admin Dashboard
                                            </Button>
                                            <Button
                                                onClick={() => openQuickLink('/api/health', 'API Health Check')}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-green-500/50 text-green-300 hover:bg-green-900/20 hover:border-green-500"
                                            >
                                                <Activity className="w-3 h-3 mr-2" />
                                                API Health
                                            </Button>
                                            <Button
                                                onClick={() => openQuickLink('/api/status', 'Service Status')}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-yellow-500/50 text-yellow-300 hover:bg-yellow-900/20 hover:border-yellow-500"
                                            >
                                                <Zap className="w-3 h-3 mr-2" />
                                                Service Status
                                            </Button>
                                            <Button
                                                onClick={() => openQuickLink('https://portal.azure.com', 'Azure Portal')}
                                                variant="outline"
                                                size="sm"
                                                className="w-full justify-start text-xs border-blue-500/50 text-blue-300 hover:bg-blue-900/20 hover:border-blue-500"
                                            >
                                                <ExternalLink className="w-3 h-3 mr-2" />
                                                Azure Portal
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Status Info */}
                                    <div className="pt-2 border-t border-red-500/20">
                                        <div className="text-xs text-gray-400 space-y-1">
                                            <div className="flex justify-between">
                                                <span>Environment:</span>
                                                <span className="text-red-300">{process.env.NODE_ENV}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Access:</span>
                                                <span className="text-green-300">
                                                    {isAdministrator ? 'Administrator' : 'Localhost Dev'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'analytics' && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-gray-900/50 p-2 rounded border border-red-500/20">
                                            <div className="text-xs text-gray-400">Page Load</div>
                                            <div className="text-sm text-white font-mono">
                                                {formatTime(performanceMetrics.pageLoadTime)}
                                            </div>
                                        </div>
                                        <div className="bg-gray-900/50 p-2 rounded border border-red-500/20">
                                            <div className="text-xs text-gray-400">DOM Ready</div>
                                            <div className="text-sm text-white font-mono">
                                                {formatTime(performanceMetrics.domContentLoaded)}
                                            </div>
                                        </div>
                                        <div className="bg-gray-900/50 p-2 rounded border border-red-500/20">
                                            <div className="text-xs text-gray-400">First Paint</div>
                                            <div className="text-sm text-white font-mono">
                                                {formatTime(performanceMetrics.firstContentfulPaint)}
                                            </div>
                                        </div>
                                        <div className="bg-gray-900/50 p-2 rounded border border-red-500/20">
                                            <div className="text-xs text-gray-400">Re-renders</div>
                                            <div className="text-sm text-white font-mono">
                                                {performanceMetrics.renderCount}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-900/50 p-2 rounded border border-red-500/20">
                                        <div className="text-xs text-gray-400 mb-1">Last Render</div>
                                        <div className="text-xs text-white font-mono">
                                            {new Date(performanceMetrics.lastRenderTime).toLocaleTimeString()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'console' && (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">
                                            Console ({consoleEntries.length})
                                        </label>
                                        <Button
                                            onClick={clearConsole}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs border-red-500/50 text-red-300 hover:bg-red-900/20"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                    <div className="space-y-1 max-h-[300px] overflow-y-auto">
                                        {consoleEntries.slice(-20).map(entry => (
                                            <div
                                                key={entry.id}
                                                className={`p-2 rounded text-xs font-mono border-l-2 ${entry.type === 'error' ? 'bg-red-900/20 border-red-500 text-red-200' :
                                                    entry.type === 'warn' ? 'bg-yellow-900/20 border-yellow-500 text-yellow-200' :
                                                        entry.type === 'info' ? 'bg-blue-900/20 border-blue-500 text-blue-200' :
                                                            'bg-gray-900/50 border-gray-500 text-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between text-[10px] opacity-70 mb-1">
                                                    <span>{entry.type.toUpperCase()}</span>
                                                    <span>{entry.timestamp.toLocaleTimeString()}</span>
                                                </div>
                                                <div className="break-words">{entry.message}</div>
                                            </div>
                                        ))}
                                        {consoleEntries.length === 0 && (
                                            <div className="text-xs text-gray-500 text-center py-4">
                                                No console output yet
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'network' && (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="text-xs text-gray-400 uppercase tracking-wide">
                                            Network ({networkEntries.length})
                                        </label>
                                        <Button
                                            onClick={clearNetwork}
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs border-red-500/50 text-red-300 hover:bg-red-900/20"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                    <div className="space-y-1 max-h-[300px] overflow-y-auto">
                                        {networkEntries.slice(-20).map(entry => (
                                            <div
                                                key={entry.id}
                                                className={`p-2 rounded text-xs font-mono border-l-2 ${entry.status === 0 ? 'bg-red-900/20 border-red-500' :
                                                    entry.status && entry.status >= 400 ? 'bg-orange-900/20 border-orange-500' :
                                                        entry.status && entry.status >= 300 ? 'bg-yellow-900/20 border-yellow-500' :
                                                            'bg-green-900/20 border-green-500'
                                                    }`}
                                            >
                                                <div className="flex justify-between text-[10px] opacity-70 mb-1">
                                                    <span className="flex gap-2">
                                                        <span>{entry.method}</span>
                                                        <span>{entry.status || 'ERR'}</span>
                                                        {entry.duration && <span>{formatTime(entry.duration)}</span>}
                                                        {entry.size && <span>{formatSize(entry.size)}</span>}
                                                    </span>
                                                    <span>{entry.timestamp.toLocaleTimeString()}</span>
                                                </div>
                                                <div className="break-all text-gray-200">
                                                    {entry.url.replace(window.location.origin, '')}
                                                </div>
                                            </div>
                                        ))}
                                        {networkEntries.length === 0 && (
                                            <div className="text-xs text-gray-500 text-center py-4">
                                                No network requests yet
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Warning */}
                        <div className="m-3 mt-0 bg-yellow-900/20 border border-yellow-500/30 rounded p-2">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-yellow-300">
                                    Admin tools are visible to users with Administrator role or when running on localhost.
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminTools;
